import { showApiErrorToast } from '@design-system';
import { getUser } from '@features/auth/auth.slice';
import Flow from '@flowjs/flow.js';
import { ENV } from '@utils';
import type { FlowFile, FlowOptions, Flow as FlowType } from 'flowjs';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

export type ApiFile = {
    base_url: string;
    created_at: string;
    filename: string;
    id: number;
    mime_type: string;
    original_filename: string;
    size: number;
    url: string;
} & FlowFile;

export type FilesComponentsProps = {
    files: ApiFile[];
    setFiles: React.Dispatch<React.SetStateAction<ApiFile[]>>;
};

export type UseFlowTsProps = Pick<FilesComponentsProps, 'setFiles'>;

export const useFlowTs = ({ setFiles }: UseFlowTsProps) => {
    const jwt = useSelector(getUser)?.token;
    const [flow, setFlow] = useState<FlowType>();
    const [loading, setLoading] = useState(false);
    const flowRef = useRef<FlowType>();

    useEffect(() => {
        if (!jwt) return;

        const options: FlowOptions = {
            target: `${ENV.API_URL}upload`,
            headers: {
                'x-ap-resty-appname': 'ecs.energie-cote-sud.com'
            },
            chunkSize: 1024 * 1024 * 5,
            permanentErrors: [400, 401, 404, 500],
            maxChunkRetries: 5,
            chunkRetryInterval: 1000,
            method: 'POST',
            allowDuplicateUploads: true
        };

        const flowInstance = new Flow(options);

        flowRef.current = flowInstance;
        setFlow(flowInstance);

        const handleFileSuccess = (flowFile: FlowFile, apiFile: string) => {
            const parsedFile = JSON.parse(apiFile) as {
                status: string;
                file: ApiFile;
            };
            setFiles((prevFiles) => [...prevFiles, { ...parsedFile.file, ...flowFile }]);
        };

        const handleFileRemoved = (file: FlowFile) => {
            setFiles((prevFiles) => prevFiles.filter((f) => f.uniqueIdentifier !== file.uniqueIdentifier));
        };

        const handleFileError = (file: FlowFile, message: string) => {
            console.error(`Error uploading file ${file.name}:`, message);
            handleFileRemoved(file);
        };

        const handleFileAdded = (flowFile: FlowFile) => {
            const isValidFile = flowFile.file.type.startsWith('image/') || flowFile.file.type.startsWith('video/');

            if (!isValidFile) {
                showApiErrorToast({
                    message: 'Veuillez sélectionner un fichier image ou vidéo'
                });
                handleFileRemoved(flowFile);
                return;
            }

            setTimeout(() => {
                flowFile.flowObj.upload();
            }, 0);
        };

        const handleProgress = () => {
            setLoading(true);
        };

        const handleComplete = () => {
            setLoading(false);
        };

        flowInstance.on('fileAdded', handleFileAdded);
        flowInstance.on('fileRemoved', handleFileRemoved);
        flowInstance.on('fileSuccess', handleFileSuccess);
        flowInstance.on('fileError', handleFileError);
        flowInstance.on('progress', handleProgress);
        flowInstance.on('complete', handleComplete);
        return () => {
            if (flowRef.current) {
                flowRef.current.off('fileAdded');
                flowRef.current.off('fileRemoved');
                flowRef.current.off('fileSuccess');
                flowRef.current.off('fileError');
                flowRef.current.off('complete');
            }
        };
    }, [jwt, setFiles]);

    const handleFileDelete = (file: FlowFile) => {
        flow?.removeFile(file);
        setFiles((prevFiles) => prevFiles.filter((f) => f.uniqueIdentifier !== file.uniqueIdentifier));
    };

    return {
        flow,
        handleFileDelete,
        loading
    };
};
